<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { provide, watch } from "vue";
  import LineItemsRow from "./line-items-row.vue";
  import { GENERIC_ERROR } from "./constants.js";
  import { Flash } from "~/jose_modern/flash.js";

  const props = defineProps({
    items: { type: Array, required: true },
    itemCombobox: { type: [Object, Function], required: true },
    comboboxProps: { type: Object, default: () => ({}) },
    mapSelection: { type: Function, required: true },
    api: { type: Object, required: true },
    labels: { type: Object, default: () => ({}) },
    row: { type: [Object, Function], default: () => LineItemsRow },
    rowProps: { type: Object, default: () => ({}) },
    excludeAddedItems: { type: Boolean, default: true },
    duplicateKey: { type: Function, default: (item) => item.item_id },
    groupKey: { type: Function, default: null },
  });

  const emit = defineEmits(["saved-count-changed", "unsaved-changed", "items-changed"]);

  let queue = Promise.resolve();

  function runMutation(fn) {
    const result = queue.then(fn);
    queue = result.catch(() => {});
    return result;
  }

  provide("runMutation", runMutation);

  let lastKey = 0;

  function newKey() {
    lastKey += 1;
    return lastKey;
  }

  function groupKeyFor(item) {
    return props.groupKey && item?.item_id != null ? props.groupKey(item) : null;
  }

  function newEntry(item) {
    return { key: newKey(), item, id: item?.id ?? null, groupKey: groupKeyFor(item) };
  }

  let entries = _ref([...props.items.map(newEntry), newEntry(null)]);
  let focusedKey = _ref(null);
  let dirtyCount = _ref(0);
  let savedCount = _ref(props.items.length);

  watch(
    () => savedCount.value,
    (count) => emit("saved-count-changed", count),
    { immediate: true }
  );

  watch(
    () => dirtyCount.value > 0,
    (hasUnsaved) => emit("unsaved-changed", hasUnsaved),
    { immediate: true }
  );

  watch(
    () => entries.value.map((entry) => entry.item).filter(Boolean),
    (items) => emit("items-changed", items),
    { deep: true, immediate: true }
  );

  function onDirtyChanged(key, isDirty) {
    dirtyCount.value += isDirty ? 1 : -1;

    const entry = entries.value.find((e) => e.key === key);
    if (entry) entry.dirty = isDirty;
  }

  function onRowCreated(key, { id }) {
    savedCount.value += 1;
    const entry = entries.value.find((e) => e.key === key);
    if (entry) entry.id = id;
    if (entries.value[entries.value.length - 1].key === key) addNewRow();
  }

  function onRowRemove(key, { wasSaved }) {
    if (wasSaved) savedCount.value -= 1;
    entries.value = entries.value.filter((e) => e.key !== key);
  }

  function onRowItemChanged(key, item) {
    const entry = entries.value.find((e) => e.key === key);
    if (!entry) return;
    entry.item = item == null ? null : { ...entry.item, ...item };
    entry.groupKey = groupKeyFor(entry.item) ?? entry.groupKey;
  }

  function onRowInsert(key, item) {
    const index = entries.value.findIndex((e) => e.key === key);
    const entry = newEntry(item);
    entries.value.splice(index + 1, 0, entry);
    focusedKey.value = entry.key;
  }

  function addNewRow() {
    const entry = newEntry(null);
    entries.value.push(entry);
    focusedKey.value = entry.key;
  }

  // A cleared row keeps its last group key so it stays in its group instead of
  // dropping to the bottom while a new product is picked. Rows are only ever
  // reordered, never nested, so moving between groups keeps their state.
  const displayEntries = _computed(() => {
    if (!props.groupKey) return entries.value;

    const groupOrder = new Map();
    entries.value.forEach((entry) => {
      if (entry.groupKey != null && !groupOrder.has(entry.groupKey))
        groupOrder.set(entry.groupKey, groupOrder.size);
    });
    const position = (entry) => groupOrder.get(entry.groupKey) ?? Infinity;

    return [...entries.value].sort((a, b) => position(a) - position(b));
  });

  const groups = _computed(() => {
    const byKey = new Map();
    if (!props.groupKey) return byKey;

    displayEntries.value.forEach((entry) => {
      const key = groupKeyFor(entry.item);
      if (key == null) return;
      if (!byKey.has(key)) byKey.set(key, { firstKey: entry.key, lastKey: null, items: [] });
      const group = byKey.get(key);
      group.lastKey = entry.key;
      group.items.push(entry.item);
    });
    return byKey;
  });

  function groupOf(entry) {
    return props.groupKey ? groups.value.get(groupKeyFor(entry.item)) : null;
  }

  function groupProps(entry) {
    if (!props.groupKey) return {};

    const group = groupOf(entry);
    return {
      groupFirst: group == null || group.firstKey === entry.key,
      groupLast: group == null || group.lastKey === entry.key,
      groupItems: group?.items ?? [],
      removeSiblings: () => removeGroupSiblings(entry.key),
    };
  }

  function removeGroupSiblings(key) {
    const entry = entries.value.find((e) => e.key === key);
    const siblings = entries.value.filter(
      (e) => e !== entry && e.groupKey != null && e.groupKey === entry.groupKey
    );

    return runMutation(async () => {
      for (const sibling of siblings) {
        if (sibling.id != null) {
          const { response, data } = await props.api
            .remove(sibling.id)
            .catch(() => ({ response: null, data: null }));

          if (!response?.ok) {
            Flash.error(data?.error || data?.errors?.join(", ") || GENERIC_ERROR);
            return false;
          }
          savedCount.value -= 1;
        }
        entries.value = entries.value.filter((e) => e !== sibling);
      }
      return true;
    });
  }

  function groupFooterItems(entry) {
    const group = groupOf(entry);
    return group?.lastKey === entry.key ? group.items : null;
  }

  const excludedItemIds = _computed(() =>
    props.excludeAddedItems ? entries.value.map((e) => e.item?.item_id).filter((id) => id != null) : []
  );

  function keyOf(entry) {
    return entry.item?.item_id == null ? null : props.duplicateKey(entry.item);
  }

  // A row already saved under a key keeps it, so only the row that walked into the
  // collision is flagged. Rows racing for the same unsaved key fall back to order.
  function isDuplicate(entry, index) {
    const key = keyOf(entry);
    if (!entry.dirty || key == null) return false;

    return entries.value.some(
      (other, otherIndex) =>
        other !== entry && keyOf(other) === key && (!other.dirty || otherIndex < index)
    );
  }

  function addItems(items) {
    const draftIndex = entries.value.findIndex((e) => e.item == null);
    const newEntries = items.map(newEntry);
    if (draftIndex === -1) {
      entries.value.push(...newEntries);
    } else {
      entries.value.splice(draftIndex, 0, ...newEntries);
    }
  }
</script>

<template>
  <div class="line-items-grid">
    <slot name="header">
      <div class="line-items-header">
        <div>Item</div>
        <div>Quantity</div>
        <div></div>
      </div>
    </slot>
    <template v-for="entry in displayEntries" :key="entry.key">
      <component
        :is="row"
        v-bind="{ ...rowProps, ...groupProps(entry) }"
        :item="entry.item"
        :item-combobox="itemCombobox"
        :combobox-props="comboboxProps"
        :map-selection="mapSelection"
        :api="api"
        :labels="labels"
        :excluded-item-ids="excludedItemIds"
        :duplicate="isDuplicate(entry, entries.indexOf(entry))"
        :autofocus="entry.key === focusedKey"
        @created="onRowCreated(entry.key, $event)"
        @remove="onRowRemove(entry.key, $event)"
        @dirty-changed="onDirtyChanged(entry.key, $event)"
        @item-changed="onRowItemChanged(entry.key, $event)"
        @insert-row="onRowInsert(entry.key, $event)"
      />
      <slot
        v-if="groupFooterItems(entry)"
        name="group-footer"
        :group-items="groupFooterItems(entry)"
      />
    </template>
  </div>
  <a class="has-text-link mt-3" @click.prevent="addNewRow">+ Add new item</a>
  <slot name="footer" v-bind="{ excludedItemIds, addItems }" />
</template>

<style lang="scss" scoped>
  @import "~jose_modern/css";
  @import "./line-items";

  .line-items-header {
    @include line-items-columns;

    font-weight: bold;
    border-bottom: 2px solid $border;

    @media screen and (max-width: $line-items-collapse) {
      display: none;
    }
  }
</style>
